import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLive } from '../../live.jsx';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import Icon from '../../components/Icon.jsx';
import { useChat } from '../../chat/ChatProvider.jsx';
import './account.css';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// "Mon compte / My account": a side panel (sticky on desktop, a slide-in
// drawer on phones) and the page on the right; on phones a bottom tab bar
// (Dashboard, Products, + Add, Settings). Every page has its own URL. The
// summary (/api/account/summary) feeds the panel's badges and the
// dashboard, and is reloaded after changes (useAccount().reload).

const AccountCtx = createContext({ summary: null, reload: () => {} });
export const useAccount = () => useContext(AccountCtx);

// perm: what the signed-in person must be allowed (company users, migration
// 030 — the owner sees everything); staff: shown to PME representatives.
const MAIN = [
  { to: '/account/dashboard', key: 'dashboard', icon: 'grid', company: true },
  { to: '/account/products', key: 'products', icon: 'list', count: (s) => s?.products.total, perm: 'products.view|products.create' },
  { to: '/account/listings/new', key: 'add', icon: 'plus', highlight: true, perm: 'products.create' },
  { to: '/account/requests', key: 'requests', icon: 'inbox', badge: (s) => s?.requests.new, perm: 'rfq.viewAll|rfq.viewAssigned' },
  { to: '/account/messages', key: 'messages', icon: 'chat', chat: true, perm: 'chat.own|chat.all' },
  // Buying requests (migration 042): every member, Silver included.
  { to: '/account/buying-requests', key: 'buyingRequests', icon: 'search', badge: (s) => s?.buyingRequests?.newOffers },
];
const WORKSPACE = [
  { to: '/account/workspace', key: 'workspace', icon: 'briefcase', end: true, perm: 'customers.viewAll|customers.viewAssigned', staff: true },
  { to: '/account/workspace/customers', key: 'customers', icon: 'users', perm: 'customers.viewAll|customers.viewAssigned', staff: true },
  // PME representatives: products for PME's products account (migration 031).
  { to: '/account/products', key: 'products', icon: 'list', perm: 'products.view|products.create', staffOnly: true },
  { to: '/account/listings/new', key: 'add', icon: 'plus', highlight: true, perm: 'products.create', staffOnly: true },
  { to: '/account/workspace/messages', key: 'repMessages', icon: 'chat', perm: 'chat.own|chat.all', staffOnly: true },
  { to: '/account/workspace/requests', key: 'repRequests', icon: 'inbox', perm: 'rfq.viewAll|rfq.viewAssigned', staffOnly: true },
  { to: '/account/buying-requests', key: 'buyingRequests', icon: 'search', staffOnly: true, badge: (s) => s?.buyingRequests?.newOffers },
  // WhatsApp "New contacts" (migration 043): PME's workspace only (the API number is PME's).
  { to: '/account/workspace/whatsapp', key: 'waInbox', icon: 'phone', perm: 'customers.viewAll', staff: true, pmeOnly: true },
  { to: '/account/workspace/tasks', key: 'tasks', icon: 'calendar', perm: 'customers.viewAll|customers.viewAssigned', staff: true },
  { to: '/account/workspace/documents', key: 'sentDocs', icon: 'file', perm: 'docs.create|docs.approve', staff: true },
  { to: '/account/workspace/stats', key: 'stats', icon: 'chart', perm: 'customers.viewAll|customers.viewAssigned', staff: true },
];
const SETTINGS = [
  { to: '/account/company', key: 'company', icon: 'building', dot: (s) => ['pending', 'rejected'].includes(s?.company.review) && s.company.review, perm: 'profile.view' },
  { to: '/account/contact', key: 'contact', icon: 'phone', perm: 'profile.view' },
  { to: '/account/documents', key: 'documents', icon: 'file', dot: (s) => (s?.documents.rejected ? 'rejected' : s?.documents.pending ? 'pending' : null), perm: 'profile.view' },
  { to: '/account/plan', key: 'plan', icon: 'card', perm: 'billing.view' },
  { to: '/account/team', key: 'team', icon: 'users', end: true, staff: true },
  { to: '/account/team/activity', key: 'activity', icon: 'history', perm: 'users.manage' },
  { to: '/account/workspace/templates', key: 'templates', icon: 'edit', perm: 'docs.create|docs.templates|users.manage', staff: true },
  // Every signed-in user has their own mailbox (no permission needed to connect it).
  { to: '/account/mailbox', key: 'mailbox', icon: 'mail', staff: true },
  { to: '/account/signature', key: 'signature', icon: 'edit', staff: true, perm: 'docs.create|customers.viewAll|customers.viewAssigned' },
  { to: '/account/security', key: 'security', icon: 'shield', staff: true },
];
// May the signed-in person use this? (no team info = before migration 030: yes)
export const allowed = (user, perm) => !perm || !user?.team || user.team.isOwner || perm.split('|').some((p) => user.team.perms?.[p]);
export const isStaff = (user) => !!user?.team?.pme && user.role !== 'admin';
export const visibleItems = (user, list) => list.filter((n) => (isStaff(user) ? n.staff || n.staffOnly : !n.staffOnly) && allowed(user, n.perm) && !(n.company && isStaff(user)) && (!n.pmeOnly || user?.team?.pme));

const HELP = { to: '/help', key: 'help', icon: 'play' };

export function planKey(user, membership) {
  if (membership?.complimentary || user.complimentary) return 'free';
  return user.isSeller ? 'seller' : 'silver';
}

export default function AccountLayout() {
  const { t } = useI18n();
  const { user, signOut, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const { unread: chatUnread } = useChat();
  const [open, setOpen] = useState(false);
  // Desktop: the side menu folds to icons (« / »), remembered on this browser.
  const [folded, setFolded] = useState(() => { try { return localStorage.getItem('pme_side_folded') === '1'; } catch { return false; } });
  const fold = () => setFolded((f) => { try { localStorage.setItem('pme_side_folded', f ? '0' : '1'); } catch { /* private mode */ } return !f; });
  const panelRef = useRef(null);
  const menuBtn = useRef(null);

  const reload = useCallback(() => api('/account/summary').then(setSummary).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload, location.pathname]);
  useLive(['account'], () => { reload(); });
  // Company users: rights changed by the owner apply without signing out —
  // the session is re-read on each page, when the tab comes back, and every minute.
  useEffect(() => { if (user?.team) refresh().catch(() => {}); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.team) return undefined;
    const again = () => { if (!document.hidden) refresh().catch(() => {}); };
    const tm = setInterval(again, 60 * 1000);
    document.addEventListener('visibilitychange', again);
    return () => { clearInterval(tm); document.removeEventListener('visibilitychange', again); };
  }, [!!user?.team]); // eslint-disable-line react-hooks/exhaustive-deps
  // Drawer: closes on navigation and Escape; focus moves into it.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.querySelector('a, button')?.focus();
    const esc = (e) => { if (e.key === 'Escape') { setOpen(false); menuBtn.current?.focus(); } };
    document.addEventListener('keydown', esc);
    document.body.classList.add('acc-drawer-open');
    return () => { document.removeEventListener('keydown', esc); document.body.classList.remove('acc-drawer-open'); };
  }, [open]);

  const s = summary;
  const plan = planKey(user, s?.membership);
  const pct = s?.completeness.percent;
  const item = (n) => {
    const count = n.count?.(s);
    const badge = n.chat ? chatUnread : n.badge?.(s);
    const dot = n.dot?.(s);
    return (
      <NavLink key={n.key} to={n.to} end={n.end} title={folded ? t(`accountNav.${n.key}`) : undefined} className={({ isActive }) => `acc-link${isActive || (n.key === 'products' && /^\/account\/listings\/\d+/.test(location.pathname)) ? ' is-active' : ''}${n.highlight ? ' acc-link-add' : ''}`}>
        <Icon name={n.icon} size={20} />
        <span className="acc-link-label">{t(`accountNav.${n.key}`)}</span>
        {badge > 0 && <span className="acc-badge acc-badge-new" aria-label={t('accountNav.newCount', { n: badge })}>{badge}</span>}
        {!badge && count > 0 && <span className="acc-badge" aria-label={t('accountNav.count', { n: count })}>{count}</span>}
        {dot && <span className={`acc-dot acc-dot-${dot}`} aria-label={t(`accountNav.dot.${dot}`)} />}
      </NavLink>
    );
  };
  const logout = () => { signOut(); navigate('/'); };

  return (
    <AccountCtx.Provider value={{ summary, reload: () => { reload(); refresh().catch(() => {}); } }}>
      <div className={`acc${folded ? ' is-folded' : ''}`}>
        <div className="acc-mobilebar">
          <button ref={menuBtn} type="button" className="acc-menu-btn" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="acc-panel">
            <Icon name="menu" size={22} /><span>{t('accountNav.menu')}</span>
          </button>
          <span className="acc-mobilebar-name">{user.companyName || user.name}</span>
        </div>

        <aside id="acc-panel" ref={panelRef} className={`acc-side${open ? ' is-open' : ''}`} aria-label={t('accountNav.title')}>
          <div className="acc-side-inner">
            <button type="button" className="acc-close" onClick={() => { setOpen(false); menuBtn.current?.focus(); }} aria-label={t('common.close')}><Icon name="close" size={20} /></button>
            <button type="button" className="acc-fold" onClick={fold} aria-expanded={!folded} aria-label={folded ? t('accountNav.unfold') : t('accountNav.fold')} title={folded ? t('accountNav.unfold') : t('accountNav.fold')}>{folded ? '»' : '«'}</button>
            <div className="acc-id">
              <Link to="/account/company" className="acc-logo" aria-label={t('accountNav.company')}>
                {s?.user.logoUrl ? <img src={s.user.logoUrl} alt="" /> : <span aria-hidden="true">{(user.companyName || user.name || '?').slice(0, 1)}</span>}
              </Link>
              <div className="acc-id-text">
                <strong className="acc-company">{user.companyName || user.name}</strong>
                {user.team?.isPerson && <span className="acc-person small">{user.name}{user.person?.jobTitle ? ` · ${user.person.jobTitle}` : ''}</span>}
                <span className="acc-badges">
                  {!isStaff(user) && allowed(user, 'billing.view') && <span className={`acc-plan acc-plan-${plan}`}>{t(`accountNav.planBadge.${plan}`)}</span>}
                  {s?.verified && <span className="acc-verified" title={t('accountNav.verifiedHint')}>✓ {t('accountNav.verified')}</span>}
                </span>
              </div>
            </div>
            {pct !== undefined && !isStaff(user) && allowed(user, 'profile.edit') && (
              <Link to="/account/dashboard#completeness" className="acc-progress" aria-label={t('accountNav.completeness', { n: pct })}>
                <span className="acc-progress-top"><span>{t('accountNav.profile')}</span><strong>{pct} %</strong></span>
                <span className="acc-progress-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
              </Link>
            )}
            <nav className="acc-nav">
              {visibleItems(user, MAIN).length > 0 && <p className="acc-group">{t('accountNav.main')}</p>}
              {visibleItems(user, MAIN).map(item)}
              {visibleItems(user, WORKSPACE).length > 0 && <p className="acc-group">{t('accountNav.workspaceGroup')}</p>}
              {visibleItems(user, WORKSPACE).map(item)}
              <div className="acc-spacer" />
              <p className="acc-group">{t('accountNav.settings')}</p>
              {visibleItems(user, SETTINGS).map(item)}
              {item(HELP)}
              <button type="button" className="acc-link acc-logout" onClick={logout} title={folded ? t('nav.logout') : undefined}><Icon name="logout" size={20} /><span className="acc-link-label">{t('nav.logout')}</span></button>
            </nav>
          </div>
        </aside>
        {open && <div className="acc-scrim" onClick={() => setOpen(false)} aria-hidden="true" />}

        <div className="acc-main">
          <Outlet />
        </div>

        <nav className="acc-tabbar" aria-label={t('accountNav.quick')}>
          {isStaff(user) || !allowed(user, 'products.view') ? (
            <>
              <NavLink to="/account/workspace" end className={({ isActive }) => (isActive ? 'is-active' : '')}><Icon name="briefcase" size={22} /><span>{t('accountNav.workspaceShort')}</span></NavLink>
              <NavLink to="/account/workspace/customers" className={({ isActive }) => (isActive ? 'is-active' : '')}><Icon name="users" size={22} /><span>{t('accountNav.customersShort')}</span></NavLink>
              <NavLink to={isStaff(user) ? '/account/workspace/messages' : '/account/messages'} className={({ isActive }) => (isActive ? 'is-active' : '')}><Icon name="chat" size={22} /><span>{t('accountNav.messagesShort')}</span></NavLink>
            </>
          ) : (
            <>
              <NavLink to="/account/dashboard" className={({ isActive }) => (isActive ? 'is-active' : '')}><Icon name="grid" size={22} /><span>{t('accountNav.dashboardShort')}</span></NavLink>
              <NavLink to="/account/products" className={({ isActive }) => (isActive ? 'is-active' : '')}><Icon name="list" size={22} /><span>{t('accountNav.productsShort')}</span></NavLink>
              {allowed(user, 'products.create') ? <NavLink to="/account/listings/new" className={({ isActive }) => `acc-tab-add${isActive ? ' is-active' : ''}`}><span className="acc-tab-plus"><Icon name="plus" size={24} /></span><span>{t('accountNav.addShort')}</span></NavLink>
                : <NavLink to="/account/workspace" end className={({ isActive }) => (isActive ? 'is-active' : '')}><Icon name="briefcase" size={22} /><span>{t('accountNav.workspaceShort')}</span></NavLink>}
            </>
          )}
          <NavLink to="/account/settings" className={({ isActive }) => (isActive || SETTINGS.some((x) => location.pathname.startsWith(x.to)) ? 'is-active' : '')}><Icon name="settings" size={22} /><span>{t('accountNav.settingsShort')}</span></NavLink>
        </nav>
      </div>
    </AccountCtx.Provider>
  );
}

// /account: the dashboard, or "Mon espace" for representatives and users
// without the products screens.
export function AccountIndex() {
  const { user } = useAuth();
  return <Navigate to={isStaff(user) || !allowed(user, 'products.view') ? '/account/workspace' : '/account/dashboard'} replace />;
}
// A page the signed-in person may not use (company users): a clear note
// instead of an error (the server refuses anyway).
export function Guard({ perm, children }) {
  const { t } = useI18n();
  const { user } = useAuth();
  // PME representatives use the product screens too (PME's products account).
  if (allowed(user, perm) && !(isStaff(user) && !perm.startsWith('products.'))) return children;
  return (
    <div className="acc-page">
      <div className="acc-empty card"><Icon name="lock" size={28} /><h2>{t('accountNav.noAccessTitle')}</h2><p className="muted">{t('accountNav.noAccess')}</p>
        <Link className="btn btn-primary" to="/account/workspace">{t('accountNav.toWorkspace')}</Link></div>
    </div>
  );
}

// Page heading used by every member-area page.
// place: its tutorials ("▶ Watch how", tutorials/places.js).
export function AccountHead({ title, lead, actions, place }) {
  return (
    <div className="acc-head">
      {place && <TutorialSlot place={place} />}
      <div>
        <h1>{title}</h1>
        {lead && <p className="muted">{lead}</p>}
      </div>
      {actions && <div className="acc-head-actions">{actions}</div>}
    </div>
  );
}

// Phone "Settings" tab: the settings pages as a list.
export function SettingsIndex() {
  const { t } = useI18n();
  const { summary: s } = useAccount();
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="acc-page">
      <AccountHead title={t('accountNav.settings')} />
      <ul className="acc-settings-list">
        {[...visibleItems(user, SETTINGS), HELP].map((n) => {
          const dot = n.dot?.(s);
          return (
            <li key={n.key}>
              <Link to={n.to}><Icon name={n.icon} size={22} /><span><strong>{t(`accountNav.${n.key}`)}</strong><small>{t(`accountNav.hint.${n.key}`)}</small></span>{dot && <span className={`acc-dot acc-dot-${dot}`} aria-label={t(`accountNav.dot.${dot}`)} />}<Icon name="arrow" size={18} /></Link>
            </li>
          );
        })}
        <li><button type="button" onClick={() => { signOut(); navigate('/'); }}><Icon name="logout" size={22} /><span><strong>{t('nav.logout')}</strong></span></button></li>
      </ul>
    </div>
  );
}
